/**
 * Frontend view — block-native inputs drive the ROI display and are
 * passed to their Gravity Form hidden field counterparts.
 *
 * Block owns:
 * - the user-facing controls, read via data-input=""
 * - .roi-calculator__results elements written via data-slot=""
 *
 * Gravity Form owns:
 * - Its own DOM inside .roi-calculator__form. We write block input
 *   values + all block-computed results into the matching GF inputs.
 */

import { calculate } from './calc.js';

// Live inputs the user manipulates, mirrored 1:1 into the form.
const LIVE_INPUT_MAP = {
	facility:   'input_102',
	gxtPerMo:   'input_1',
	rmrPerMo:   'input_4',
	gxtPrice:   'input_3',
	rmrPrice:   'input_5',
	hourlyRate: 'input_6',
	currency:   'input_7',
};

// Intermediate calc fields on the form. We compute + write these so the entry stored
// in GF has the full breakdown for downstream (notifications, AC, etc).
const CALC_FIELD_MAP = {
	totalTests:    'input_73',
	employeeCost:  'input_16',
	requiredKits:  'input_17',
	discsLeftover: 'input_33',
	y1Required:    'input_38',
	y2Required:    'input_34',
};

// Per-currency Y1/Y2 revenue/expenses/profit fields on the form.
const CURRENCY_RESULT_FIELDS = {
	USD: { y1Revenue: 'input_9',   y1Expenses: 'input_32', y1Profit: 'input_11',
	       y2Revenue: 'input_19',  y2Expenses: 'input_20', y2Profit: 'input_21' },
	CAD: { y1Revenue: 'input_26',  y1Expenses: 'input_27', y1Profit: 'input_28',
	       y2Revenue: 'input_29',  y2Expenses: 'input_30', y2Profit: 'input_31' },
	EUR: { y1Revenue: 'input_92',  y1Expenses: 'input_91', y1Profit: 'input_90',
	       y2Revenue: 'input_98',  y2Expenses: 'input_97', y2Profit: 'input_96' },
	GBP: { y1Revenue: 'input_95',  y1Expenses: 'input_94', y1Profit: 'input_93',
	       y2Revenue: 'input_101', y2Expenses: 'input_100', y2Profit: 'input_99' },
};

function readInputs(root) {
	const values = {};
	Object.keys(LIVE_INPUT_MAP).forEach((key) => {
		const el = root.querySelector(`[data-input="${key}"]`);
		values[key] = el ? el.value : '';
	});
	return values;
}

function formatMoney(n, currency) {
	// USD and CAD render as "USD$52,032" / "CAD$52,032"
	// EUR and GBP render with their native symbol via Intl (€, £)
	if (currency === 'USD' || currency === 'CAD') {
		const amount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
		return `${currency}$${amount}`;
	}
	try {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency,
			currencyDisplay: 'symbol',
			maximumFractionDigits: 0,
		}).format(n);
	} catch (e) {
		return `${currency} ${Math.round(n).toLocaleString()}`;
	}
}

function writeGfField(gform, name, value) {
	const el = gform.querySelector(`[name="${name}"]`);
	if (!el) return;
	el.value = value;
}

function syncToGravityForm(root, inputs, result) {
	const gform = root.querySelector('form[id^="gform_"]');
	if (!gform) return;

	// 1. Live inputs
	Object.entries(LIVE_INPUT_MAP).forEach(([key, name]) => {
		writeGfField(gform, name, inputs[key]);
	});

	// 2. Intermediate calc fields
	Object.entries(CALC_FIELD_MAP).forEach(([key, name]) => {
		writeGfField(gform, name, result[key]);
	});

	// 3. Per-currency Y1/Y2 revenue / expenses / profit.
	// Only the active currency's fields hold values; others are cleared.
	Object.entries(CURRENCY_RESULT_FIELDS).forEach(([cur, fields]) => {
		const active = cur === result.currency;
		writeGfField(gform, fields.y1Revenue,  active ? result.revenue    : '');
		writeGfField(gform, fields.y1Expenses, active ? result.y1Expenses : '');
		writeGfField(gform, fields.y1Profit,   active ? result.y1Profit   : '');
		writeGfField(gform, fields.y2Revenue,  active ? result.revenue    : '');
		writeGfField(gform, fields.y2Expenses, active ? result.y2Expenses : '');
		writeGfField(gform, fields.y2Profit,   active ? result.y2Profit   : '');
	});
}

function update(root, prices) {
	const inputs = readInputs(root);
	const result = calculate(inputs, prices);

	const set = (slot, value) => {
		const el = root.querySelector(`[data-slot="${slot}"]`);
		if (el) el.textContent = value;
	};

	set('y1-revenue',  formatMoney(result.revenue,    result.currency));
	set('y1-expenses', formatMoney(result.y1Expenses, result.currency));
	set('y1-profit',   formatMoney(result.y1Profit,   result.currency));
	set('y2-revenue',  formatMoney(result.revenue,    result.currency));
	set('y2-expenses', formatMoney(result.y2Expenses, result.currency));
	set('y2-profit',   formatMoney(result.y2Profit,   result.currency));

	// Mirror slider values into their <output>.
	root.querySelectorAll('[data-output-for]').forEach((out) => {
		const key = out.dataset.outputFor;
		if (inputs[key] !== undefined) out.textContent = inputs[key];
	});

	syncToGravityForm(root, inputs, result);
}

function parseJsonDataset(root, key) {
	try {
		return JSON.parse(root.dataset[key] || '{}');
	} catch (e) {
		return {};
	}
}

// When the visitor picks a facility, snap the exercise + RMR sliders to the
// defaults configured for that facility (block attribute `facilityDefaults`).
function applyFacilityDefaults(root, facilityDefaults) {
	const facilityEl = root.querySelector('[data-input="facility"]');
	if (!facilityEl) return;
	const row = facilityDefaults[facilityEl.value];
	if (!row) return;

	const gxtEl = root.querySelector('[data-input="gxtPerMo"]');
	const rmrEl = root.querySelector('[data-input="rmrPerMo"]');
	if (gxtEl && row.exercise !== undefined) gxtEl.value = row.exercise;
	if (rmrEl && row.rmr !== undefined) rmrEl.value = row.rmr;
}

function init(root) {
	const prices = parseJsonDataset(root, 'prices');
	const facilityDefaults = parseJsonDataset(root, 'facilityDefaults');

	const inputsWrap = root.querySelector('.roi-calculator__inputs');
	if (!inputsWrap) return;

	inputsWrap.addEventListener('input', () => update(root, prices));
	inputsWrap.addEventListener('change', (ev) => {
		if (ev.target && ev.target.matches('[data-input="facility"]')) {
			applyFacilityDefaults(root, facilityDefaults);
		}
		update(root, prices);
	});

	applyFacilityDefaults(root, facilityDefaults);
	update(root, prices);
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.roi-calculator').forEach(init);
});

// GF re-renders the form on AJAX validation / conditional logic. Re-sync
// after any GF render event so the hidden fields hold current values.
if (typeof window !== 'undefined' && window.jQuery) {
	window.jQuery(document).on('gform_post_render', function (_ev, gformId) {
		document.querySelectorAll('.roi-calculator').forEach((root) => {
			const dataFormId = root.dataset.formId;
			if (!dataFormId || String(dataFormId) === String(gformId)) {
				update(root, parseJsonDataset(root, 'prices'));
			}
		});
	});
}
